import { z } from "zod";

import { ToDoTagInfoSchema } from "@/schemas/tag.schema";
import { ERROR_TYPE } from "@/utils";

import { ToDoPriority } from "../enum/ToDoPriority.enum";
import { ToDoStatus } from "../enum/ToDoStatus.enum";

// Re-export from shared schema for backward compatibility
export { ToDoTagInfoSchema, ToDoTagResponseSchema, type ToDoTagInfo } from "@/schemas/tag.schema";

const { REQUIRED } = ERROR_TYPE;

export const SubTaskInfoSchema = z.object({
	id: z.number().optional(),
	title: z.string().min(1, { message: REQUIRED }).trim(),
	description: z.string().optional(),
	status: z.nativeEnum(ToDoStatus).optional(),
	priority: z.nativeEnum(ToDoPriority).optional(),
	complexityEstimate: z.number().optional(),
	tagId: z.number().optional(),
	tag: ToDoTagInfoSchema.optional(),
	assigneeId: z.number().optional(),
	taskId: z.number(),
	order: z.number().optional(),
	createdAt: z.date().optional(),
	updatedAt: z.date().optional(),
	deletedAt: z.date().optional(),
	isTask: z.boolean().default(false),
});

export const TaskInfoSchema = z.object({
	id: z.number().optional(),
	title: z.string().optional(),
	description: z.string().optional(),
	status: z.nativeEnum(ToDoStatus).optional(),
	priority: z.nativeEnum(ToDoPriority).optional(),
	complexityEstimate: z.number().optional(),
	tagId: z.number().optional(),
	tag: ToDoTagInfoSchema.optional(),
	assigneeId: z.number().optional(),
	order: z.number().optional(),
	subTasks: z.array(SubTaskInfoSchema).optional(),
	createdAt: z.date().optional(),
	updatedAt: z.date().optional(),
	deletedAt: z.date().optional(),
	isTask: z.boolean().default(true),
});

export const CreateTaskInfoSchema = z.object({
	title: z.string().min(1, { message: REQUIRED }).trim(),
	description: z.string().optional(),
	status: z.nativeEnum(ToDoStatus).optional(),
	priority: z.nativeEnum(ToDoPriority),
	complexityEstimate: z.number(),
	tagId: z.number().optional(),
	assigneeId: z.number().optional().nullable(),
});

export const CreateSubTaskInfoSchema = CreateTaskInfoSchema.extend({
	taskId: z.number().min(1, { message: REQUIRED }),
	title: z.string().min(1, { message: REQUIRED }).trim(),
	description: z.string().optional().nullable(),
	status: z.nativeEnum(ToDoStatus).optional().nullable(),
	priority: z.nativeEnum(ToDoPriority).optional().nullable(),
	complexityEstimate: z.number().optional().nullable(),
	tagId: z.number().optional().nullable(),
	assigneeId: z.number().optional().nullable(),
});

export const UpdateTaskInfoSchema = z.object({
	id: z.number(),
	title: z.string().min(1, { message: REQUIRED }),
	description: z.string().optional().nullable(),
	status: z.nativeEnum(ToDoStatus).optional(),
	priority: z.nativeEnum(ToDoPriority).optional().nullable(),
	complexityEstimate: z.number().optional().nullable(),
	tagId: z.number().optional().nullable(),
	assigneeId: z.number().optional().nullable(),
});

export const CompanySchema = z.object({
	id: z.number(),
	name: z.string(),
});

export const AssigneeSchema = z.object({
	id: z.number(),
	firstName: z.string(),
	lastName: z.string(),
	companyId: CompanySchema,
});

export const UpdateSubTaskInfoSchema = UpdateTaskInfoSchema.extend({
	taskId: z.number(),
});

export const DeleteTaskResponseSchema = z.object({
	id: z.number(),
});

export const UserTasksResponseSchema = z.array(
	z.discriminatedUnion("isTask", [
		TaskInfoSchema.extend({ isTask: z.literal(true) }),
		SubTaskInfoSchema.extend({ isTask: z.literal(false) }),
	])
);

export const TaskResponseSchema = z.array(TaskInfoSchema);
export const SubTaskResponseSchema = z.array(SubTaskInfoSchema);

export type TaskInfo = z.infer<typeof TaskInfoSchema>;
export type Assignee = z.infer<typeof AssigneeSchema>;
export type SubTaskInfo = z.infer<typeof SubTaskInfoSchema>;
export type TaskResponse = z.infer<typeof TaskResponseSchema>;
export type SubTaskResponse = z.infer<typeof SubTaskResponseSchema>;
export type CreateTaskInfo = z.infer<typeof CreateTaskInfoSchema>;
export type CreateSubTaskInfo = z.infer<typeof CreateSubTaskInfoSchema>;
export type UpdateTaskInfo = z.infer<typeof UpdateTaskInfoSchema>;
export type UpdateSubTaskInfo = z.infer<typeof UpdateSubTaskInfoSchema>;
export type DeleteTaskResponse = z.infer<typeof DeleteTaskResponseSchema>;
